import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './Dashboard.css';
import './PerformanceDashboard.css';
import NoTargetsPlaceholder from './NoTargetsPlaceholder';
import DatePicker from './DatePicker';
import { toLocalDateString } from '../utils/formatters';
import { utcDateKey, utcWeekKey, weekKeyOf, localDateKey } from '../utils/dateWindow';
import { getDocFee, docFeeIncome } from '../utils/docFee';
import { overUnderFor, avgOverUnder, tradeCount } from '../utils/overUnder';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function calculatePercent(actual, target, lowerIsBetter = false) {
  if (!target || target === 0) return 0;
  if (lowerIsBetter) {
    // For "lower is better" ratios (e.g. greets-per-sale), being under the
    // target ratio is good: percent = target / actual. No actual yet = 0%.
    if (!actual || actual <= 0) return 0;
    return Math.round((target / actual) * 100);
  }
  return Math.round((actual / target) * 100);
}

// Abbreviate numbers: 91K, 1.2M, 3.4B, etc.
function abbreviateNumber(value) {
  if (value === undefined || value === null || isNaN(value)) return '0';
  const abs = Math.abs(value);
  if (abs >= 1e9) return (value / 1e9).toFixed(abs >= 1e10 ? 0 : 1) + 'B';
  if (abs >= 1e6) return (value / 1e6).toFixed(abs >= 1e7 ? 0 : 1) + 'M';
  if (abs >= 1e3) return (value / 1e3).toFixed(abs >= 1e4 ? 0 : 1) + 'K';
  return value.toLocaleString();
}

function formatCurrency(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '$0';
  return '$' + abbreviateNumber(Math.round(value));
}

function formatNumber(value) {
  if (value === undefined || value === null || Number.isNaN(value)) return '0';
  return abbreviateNumber(Number(value));
}

// Compute fiscal meta from dealership settings
function getFiscalMetaForDealership(dealership, referenceDate = new Date()) {
  const mode = dealership?.performanceYearMode || 'CALENDAR';
  const startMonth = Math.min(Math.max(Number(dealership?.fiscalYearStartMonth) || 1, 1), 12);
  const startDay = Math.min(Math.max(Number(dealership?.fiscalYearStartDay) || 1, 1), 28);
  const today = new Date(referenceDate);
  let fiscalStart = new Date(today.getFullYear(), startMonth - 1, startDay, 0, 0, 0, 0);
  if (mode === 'FISCAL' && today < fiscalStart) {
    fiscalStart.setFullYear(fiscalStart.getFullYear() - 1);
  }
  const fiscalEnd = new Date(fiscalStart);
  fiscalEnd.setFullYear(fiscalStart.getFullYear() + 1);
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysInYear = Math.round((fiscalEnd.getTime() - fiscalStart.getTime()) / msPerDay);
  const daysElapsed = Math.max(0, Math.floor((today.getTime() - fiscalStart.getTime()) / msPerDay));
  const weeksInYear = Math.ceil(daysInYear / 7);
  const monthsInYear = 12;
  return { fiscalStart, fiscalEnd, daysInYear, daysElapsed, weeksInYear, monthsInYear };
}

// Get monthly target value from monthlyTargets array for current month
function getMonthlyTarget(targets, field, currentMonth) {
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    return 0;
  }

  // currentMonth is 0-based (0 = January), monthlyTargets uses 1-based (1 = January)
  const lookupMonth = currentMonth + 1;
  console.log(`getMonthlyTarget: field=${field}, currentMonth=${currentMonth}, lookupMonth=${lookupMonth}`);

  const monthData = targets.monthlyTargets.find(m => m.month === lookupMonth);
  console.log(`getMonthlyTarget: monthData for month ${lookupMonth}:`, monthData);

  if (!monthData) return 0;

  // Map old field names to monthly target field names
  let result = 0;
  switch (field) {
    case 'unitSales':
      result = (Number(monthData.newUnits) || 0) + (Number(monthData.usedUnits) || 0);
      console.log(`getMonthlyTarget: unitSales = ${monthData.newUnits} + ${monthData.usedUnits} = ${result}`);
      break;
    case 'grossProfit':
      result = (Number(monthData.newGrossProfit) || 0) +
             (Number(monthData.usedGrossProfit) || 0) +
             (Number(monthData.backEndGrossProfit) || 0);
      console.log(`getMonthlyTarget: grossProfit = ${monthData.newGrossProfit} + ${monthData.usedGrossProfit} + ${monthData.backEndGrossProfit} = ${result}`);
      break;
    default:
      result = Number(monthData[field]) || 0;
      console.log(`getMonthlyTarget: ${field} = ${result}`);
  }
  return result;
}

// Sum monthly targets from fiscal year start to current month (MTD)
function getMTDTarget(targets, field, dealership, currentMonth) {
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    return 0;
  }

  // Get fiscal year start month (1-based from dealership settings, default = 1 for January)
  const fiscalStartMonth = dealership?.fiscalYearStartMonth || 1;

  // Convert to 0-based for calculations
  const fiscalStartMonth0 = fiscalStartMonth - 1;

  // Determine which months to sum
  let monthsToSum = [];

  if (currentMonth >= fiscalStartMonth0) {
    // Same calendar year as fiscal start (e.g., fiscal starts Aug, currently Dec)
    for (let m = fiscalStartMonth0; m <= currentMonth; m++) {
      monthsToSum.push(m + 1); // Convert back to 1-based for lookup
    }
  } else {
    // Different calendar year (e.g., fiscal starts Aug, currently Feb means Aug-Dec last year + Jan-Feb this year)
    // For now, we'll just sum from Jan to current month for simplicity
    // A full implementation would need fiscal year tracking
    for (let m = 0; m <= currentMonth; m++) {
      monthsToSum.push(m + 1);
    }
  }

  // Sum the target values for all months
  let total = 0;
  for (const month of monthsToSum) {
    const monthData = targets.monthlyTargets.find(m => m.month === month);
    if (monthData) {
      // Map old field names to monthly target field names
      switch (field) {
        case 'unitSales':
          total += (Number(monthData.newUnits) || 0) + (Number(monthData.usedUnits) || 0);
          break;
        case 'grossProfit':
          total += (Number(monthData.newGrossProfit) || 0) +
                   (Number(monthData.usedGrossProfit) || 0) +
                   (Number(monthData.backEndGrossProfit) || 0);
          break;
        default:
          total += Number(monthData[field]) || 0;
      }
    }
  }

  return total;
}

// Convert stored target to requested period using fiscal-aware counts
// This is used for activity metrics (greets, sit-downs, etc.) that still use frequency selectors
function convertTargetValue(targets, field, period, fiscalMeta) {
  if (!targets) return 0;
  const base = Number(targets[field]) || 0;
  const activityFields = ['greets', 'sitDowns', 'writeUps', 'testRides'];
  const storedFreq = activityFields.includes(field)
    ? (targets.metricsFrequency || 'daily')
    : (targets.frontEndFrequency || 'yearly');

  const fromFreq = (storedFreq || 'yearly').toLowerCase();
  const toFreq = (period || 'yearly').toLowerCase();

  // Convert to yearly total first
  let yearlyTotal;
  switch (fromFreq) {
    case 'daily':
      yearlyTotal = base * fiscalMeta.daysInYear;
      break;
    case 'weekly':
      yearlyTotal = base * fiscalMeta.weeksInYear;
      break;
    case 'monthly':
      yearlyTotal = base * fiscalMeta.monthsInYear;
      break;
    case 'yearly':
    default:
      yearlyTotal = base;
  }

  // Convert yearly total to requested display period
  switch (toFreq) {
    case 'daily':
      return Math.round(yearlyTotal / fiscalMeta.daysInYear);
    case 'weekly':
      return Math.round(yearlyTotal / fiscalMeta.weeksInYear);
    case 'monthly':
      return Math.round(yearlyTotal / fiscalMeta.monthsInYear);
    case 'yearly':
    default:
      return Math.round(yearlyTotal);
  }
}

// Get target for a specific period using monthly targets
function getTargetForPeriod(targets, field, period, dealership, refDate) {
  const now = new Date(refDate);
  const currentMonth = now.getMonth(); // 0-based
  const currentYear = now.getFullYear();

  console.log(`getTargetForPeriod: refDate=${refDate}, now=${now.toISOString()}, currentMonth=${currentMonth}, period=${period}, field=${field}`);

  // If no monthly targets, fall back to old convertTargetValue behavior
  if (!targets || !targets.monthlyTargets || !Array.isArray(targets.monthlyTargets)) {
    const fiscalMeta = getFiscalMetaForDealership(dealership);
    return convertTargetValue(targets, field, period, fiscalMeta);
  }

  switch (period) {
    case 'daily': {
      // Daily target = current month's target ÷ days in current month
      const monthlyTarget = getMonthlyTarget(targets, field, currentMonth);
      const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
      return Math.round(monthlyTarget / daysInMonth);
    }

    case 'weekly': {
      // Weekly target = current month's target ÷ weeks in current month (~4.33)
      const monthlyTarget = getMonthlyTarget(targets, field, currentMonth);
      const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
      const weeksInMonth = daysInMonth / 7;
      return Math.round(monthlyTarget / weeksInMonth);
    }

    case 'monthly': {
      // Monthly target = specific month's target
      return getMonthlyTarget(targets, field, currentMonth);
    }

    case 'yearly': {
      // Yearly target = sum of all 12 monthly targets
      let total = 0;
      for (let m = 1; m <= 12; m++) {
        const monthData = targets.monthlyTargets.find(mt => mt.month === m);
        if (monthData) {
          total += Number(monthData[field]) || 0;
        }
      }
      return total;
    }

    default:
      return 0;
  }
}

const initMetricBlock = () => ({
  daily: { value: 0, target: 0, percent: 0, extra: null },
  weekly: { value: 0, target: 0, percent: 0, extra: null },
  monthly: { value: 0, target: 0, percent: 0, extra: null },
  yearly: { value: 0, target: 0, percent: 0, extra: null }
});

// --- Leaderboard Widget ---
function SalesLeaderboard({ selectedDealerships, startDate, endDate, period, onPeriodChange }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);

  useEffect(() => {
    async function fetchLeaderboard() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const params = new URLSearchParams();
        if (selectedDealerships && selectedDealerships.length > 0) {
          selectedDealerships.forEach(id => params.append('dealershipIds', id));
        }
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (period) params.append('period', period);
        const url = `${API_URL}/api/analytics/leaderboard/sales?${params.toString()}`;
        const res = await fetch(url, { headers });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Failed to load leaderboard (status ${res.status}): ${text}`);
        }
        const json = await res.json();
        setData(json.leaderboard || []);
      } catch (err) {
        setError(err.message || 'Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    }
    fetchLeaderboard();
  }, [selectedDealerships, startDate, endDate, period]);

  if (loading) return <div className="placeholder-view"><p>Loading leaderboard...</p></div>;
  if (error) return <div className="placeholder-view error"><p>{error}</p></div>;

  return (
    <div className="card leaderboard-card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 className="card-title">Sales Leaderboard</h2>
          <p className="card-subtitle">Top performing fit specialists</p>
        </div>
        <div>
          <select value={period} onChange={e => onPeriodChange(e.target.value)} className="leaderboard-period-select">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>
      <div className="leaderboard-table-wrapper">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Name</th>
              <th>Dealership</th>
              <th>New Bikes</th>
              <th>Used Bikes</th>
              <th>Greets</th>
              <th>Sit Downs</th>
              <th>Test Rides</th>
              <th>Write Ups</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '24px', color: 'rgba(255,255,255,0.5)' }}>
                  No sales logged yet for this period.
                </td>
              </tr>
            ) : (
              data.map(row => (
                <tr key={row.employeeId}>
                  <td>{row.rank}</td>
                  <td>{row.name}</td>
                  <td>{row.dealershipName}</td>
                  <td>{row.newBikesSold || 0}</td>
                  <td>{row.usedBikesSold || 0}</td>
                  <td>{row.greets || 0}</td>
                  <td>{row.sitDowns || 0}</td>
                  <td>{row.testRides || 0}</td>
                  <td>{row.writeUps || 0}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SalesPerformanceDashboard({ user, managedOwner }) {
  const location = useLocation();
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealerships, setSelectedDealerships] = useState([]);
  const [metricsByDealer, setMetricsByDealer] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  // Default to monthly alone: teams enter data at the end of the day and often
  // at the end of the week, so the shorter ranges sit blank or read as
  // under-performing for most of the period they cover. The other ranges stay
  // one click away in the View selector.
  const [selectedPeriods, setSelectedPeriods] = useState(['monthly']);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('monthly');
  const [selectedDate, setSelectedDate] = useState(new Date());
  // Apply the default dealership selection only once (on first load) so that
  // changing the date or other state doesn't clobber the user's own filter choice.
  const didSetInitialDealership = useRef(false);

  const isOwnerView = user?.role === 'OWNER' || managedOwner;

  const periodOrder = ['daily', 'weekly', 'monthly', 'yearly'];
  const periodLabels = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' };

  const togglePeriod = (period) => {
    if (selectedPeriods.includes(period)) {
      // Only allow deselect if more than one is selected
      if (selectedPeriods.length > 1) {
        setSelectedPeriods(selectedPeriods.filter(p => p !== period));
      }
    } else {
      // Add period (up to 4)
      if (selectedPeriods.length < 4) {
        setSelectedPeriods([...selectedPeriods, period].sort((a, b) => periodOrder.indexOf(a) - periodOrder.indexOf(b)));
      }
    }
  };

  useEffect(() => {
    // Check for dealershipId in location.state or query param
    let initialDealershipId = null;
    if (location && location.state && location.state.dealershipId) {
      initialDealershipId = location.state.dealershipId;
    } else if (location && location.search) {
      const params = new URLSearchParams(location.search);
      if (params.has('dealershipId')) {
        initialDealershipId = params.get('dealershipId');
      }
    }
    if (initialDealershipId) {
      setSelectedDealerships([initialDealershipId]);
      didSetInitialDealership.current = true;
    }
    load();
    // eslint-disable-next-line
  }, [managedOwner, selectedDate]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let url = isOwnerView ? `${API_URL}/api/owners/dealerships` : `${API_URL}/api/dealerships`;
      if (managedOwner && isOwnerView) {
        url += `?ownerId=${managedOwner.id}`;
      }

      const dsRes = await fetch(url, { headers });
      if (!dsRes.ok) throw new Error('Failed to load dealerships');
      const dsData = await dsRes.json();
      const dsList = dsData.dealerships || [];
      setDealerships(dsList);
      // Default to a single dealership on first load (when one wasn't already
      // chosen via navigation state/query). Scoping to one store avoids blending
      // fit specialists from every accessible dealership into the leaderboard —
      // platform staff (e.g. site managers) can widen the scope via the filter.
      if (!didSetInitialDealership.current) {
        didSetInitialDealership.current = true;
        if (dsList.length > 0) {
          setSelectedDealerships([dsList[0].id]);
        }
      }

      const metricsEntries = await Promise.all(
        dsList.map(async (d) => {
          const m = await fetchMetricsForDealership(d.id, headers, selectedDate);
          return [d.id, m];
        })
      );
      setMetricsByDealer(Object.fromEntries(metricsEntries));
    } catch (err) {
      console.error('Load error:', err);
      setError(err.message || 'Failed to load sales performance');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetricsForDealership = async (dealershipId, headers, referenceDate = new Date()) => {
    const refDate = new Date(referenceDate);
    const todayIso = localDateKey(refDate);
    const now = refDate;
    const month = now.getMonth();
    const year = now.getFullYear();

    try {
      const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
      const dateParam = toLocalDateString(now);
      const [logsRes, salesRes, targetsRes, profitSummaryRes, transactionMetricsRes] = await Promise.all([
        fetch(`${API_URL}/api/dealerships/${dealershipId}/daily-logs`, { headers }),
        fetch(`${API_URL}/api/dealerships/${dealershipId}/sales-activities`, { headers }),
        fetch(`${API_URL}/api/targets/${dealershipId}`, { headers }),
        fetch(`${API_URL}/api/profit-summary?dealershipIds[]=${dealershipId}&month=${monthStr}`, { headers }),
        fetch(`${API_URL}/api/dealerships/${dealershipId}/transaction-metrics?date=${dateParam}`, { headers })
      ]);

      const logsData = logsRes.ok ? await logsRes.json() : { entries: [] };
      const salesData = salesRes.ok ? await salesRes.json() : { activities: [] };
      const targetsData = targetsRes.ok ? await targetsRes.json() : { frontEnd: {} };
      const profitSummaryData = profitSummaryRes.ok ? await profitSummaryRes.json() : { dealerships: [] };
      const transactionMetrics = transactionMetricsRes.ok ? await transactionMetricsRes.json() : { daily: 0, weekly: 0, monthly: 0, yearly: 0 };
      const targets = targetsData.frontEnd || {};
      const profitMetrics = profitSummaryData.dealerships?.[0]?.metrics || {};

      console.log('SalesPerformanceDashboard targets for', dealershipId, ':', targets);
      console.log('monthlyTargets array:', targets.monthlyTargets);

      // Find dealership settings to compute fiscal meta
      const dealership = dealerships.find(d => d.id === dealershipId) || {};
      const fiscalMeta = getFiscalMetaForDealership(dealership, refDate);

    // Weeks run Sunday through Saturday here. The reference week comes from the
    // selected date in local terms; each row's week comes from its UTC date.
    const weekStartIso = weekKeyOf(localDateKey(now), 0);

    const dailyLogs = (logsData.entries || []).filter((e) => utcDateKey(e.date) === todayIso);

    const weeklyLogs = (logsData.entries || []).filter((e) => utcWeekKey(e.date, 0) === weekStartIso);
    
    // Helper to parse UTC date and extract components without timezone conversion
    const getUTCDateComponents = (dateStr) => {
      const d = new Date(dateStr);
      return {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth(),
        date: d.getUTCDate()
      };
    };

    // MTD: all data from start of month through selected date (inclusive)
    const monthlyLogs = (logsData.entries || []).filter((e) => {
      const logDateComp = getUTCDateComponents(e.date);
      const matches = logDateComp.month === month &&
             logDateComp.year === year &&
             (logDateComp.year < year ||
              (logDateComp.year === year && logDateComp.month < month) ||
              (logDateComp.year === year && logDateComp.month === month && logDateComp.date <= now.getDate()));

      return matches;
    });

    const yearlyLogs = (logsData.entries || []).filter((e) => {
      const logDateComp = getUTCDateComponents(e.date);
      return logDateComp.year === year;
    });

    const sumLogs = (list, field) => list.reduce((sum, e) => sum + (Number(e[field]) || 0), 0);

    const greets = initMetricBlock();
    ['daily', 'weekly', 'monthly', 'yearly'].forEach(period => {
      const logs = period === 'daily' ? dailyLogs : period === 'weekly' ? weeklyLogs : period === 'monthly' ? monthlyLogs : yearlyLogs;
      greets[period].value = sumLogs(logs, 'greets');
      greets[period].target = convertTargetValue(targets, 'greets', period, fiscalMeta);
      greets[period].percent = calculatePercent(greets[period].value, greets[period].target);
    });

    const sitDowns = initMetricBlock();
    ['daily', 'weekly', 'monthly', 'yearly'].forEach(period => {
      const logs = period === 'daily' ? dailyLogs : period === 'weekly' ? weeklyLogs : period === 'monthly' ? monthlyLogs : yearlyLogs;
      sitDowns[period].value = sumLogs(logs, 'sitDowns');
      sitDowns[period].target = convertTargetValue(targets, 'sitDowns', period, fiscalMeta);
      sitDowns[period].percent = calculatePercent(sitDowns[period].value, sitDowns[period].target);
    });

    const writeUps = initMetricBlock();
    ['daily', 'weekly', 'monthly', 'yearly'].forEach(period => {
      const logs = period === 'daily' ? dailyLogs : period === 'weekly' ? weeklyLogs : period === 'monthly' ? monthlyLogs : yearlyLogs;
      writeUps[period].value = sumLogs(logs, 'writeUps');
      writeUps[period].target = convertTargetValue(targets, 'writeUps', period, fiscalMeta);
      writeUps[period].percent = calculatePercent(writeUps[period].value, writeUps[period].target);
    });

    const testRides = initMetricBlock();
    ['daily', 'weekly', 'monthly', 'yearly'].forEach(period => {
      const logs = period === 'daily' ? dailyLogs : period === 'weekly' ? weeklyLogs : period === 'monthly' ? monthlyLogs : yearlyLogs;
      testRides[period].value = sumLogs(logs, 'testRides');
      testRides[period].target = convertTargetValue(targets, 'testRides', period, fiscalMeta);
      testRides[period].percent = calculatePercent(testRides[period].value, testRides[period].target);
    });

    // When the dealership disables "always show F&I", unclosed deals don't count
    // toward any report figures until they're marked closed/committed.
    const activities = (dealership.showUnclosedFI !== false)
      ? (salesData.activities || [])
      : (salesData.activities || []).filter((a) => a.closed);
    const filterByPeriod = (list, predicate) => list.filter(predicate);
    // Row membership from the row's UTC calendar date, matching the month and
    // year tests below, which already read UTC components.
    const isToday = (d) => utcDateKey(d) === todayIso;
    const isThisWeek = (d) => utcWeekKey(d, 0) === weekStartIso;
    // MTD: check if date is in current month AND on or before today
    const isThisMonth = (d) => {
      const saleDateComp = getUTCDateComponents(d);
      return saleDateComp.month === month &&
             saleDateComp.year === year &&
             (saleDateComp.year < year ||
              (saleDateComp.year === year && saleDateComp.month < month) ||
              (saleDateComp.year === year && saleDateComp.month === month && saleDateComp.date <= now.getDate()));
    };
    const isThisYear = (d) => {
      const saleDateComp = getUTCDateComponents(d);
      return saleDateComp.year === year;
    };

    const dailyActivities = filterByPeriod(activities, (a) => isToday(a.saleDate));
    const weeklyActivities = filterByPeriod(activities, (a) => isThisWeek(a.saleDate));
    const monthlyActivities = filterByPeriod(activities, (a) => isThisMonth(a.saleDate));
    const yearlyActivities = filterByPeriod(activities, (a) => isThisYear(a.saleDate));

    // Over/under on a trade is a cost of moving the unit, so it comes out of
    // that unit's gross at report time. The stored totalCost is left alone.
    const buildSalesBlock = (list, filterNew) => {
      const subset = list.filter((a) => {
        const val = (a.motorcycleNewUsed || '').toUpperCase();
        return filterNew ? val === 'NEW' : val === 'USED';
      });
      const count = subset.length;
      const gross = subset.reduce((sum, a) => {
        const price = Number(a.salesPrice) || 0;
        const cost = Number(a.totalCost) || 0;
        return sum + (price - cost - (overUnderFor(a) || 0));
      }, 0);
      return { count, gross };
    };

    const salesByPeriod = {
      daily: { new: buildSalesBlock(dailyActivities, true), used: buildSalesBlock(dailyActivities, false) },
      weekly: { new: buildSalesBlock(weeklyActivities, true), used: buildSalesBlock(weeklyActivities, false) },
      monthly: { new: buildSalesBlock(monthlyActivities, true), used: buildSalesBlock(monthlyActivities, false) },
      yearly: { new: buildSalesBlock(yearlyActivities, true), used: buildSalesBlock(yearlyActivities, false) }
    };

    const newUnits = initMetricBlock();
    const usedUnits = initMetricBlock();
    const totalFrontend = initMetricBlock();
    const perUnit = initMetricBlock();

    ['daily', 'weekly', 'monthly', 'yearly'].forEach(period => {
      const newSales = salesByPeriod[period].new;
      const usedSales = salesByPeriod[period].used;
      const unitsTarget = getTargetForPeriod(targets, 'unitSales', period, dealership, refDate);
      const grossTarget = getTargetForPeriod(targets, 'grossProfit', period, dealership, refDate);

      // Get new/used targets directly from monthly targets if available
      const newUnitsTarget = getTargetForPeriod(targets, 'newUnits', period, dealership, refDate);
      const usedUnitsTarget = getTargetForPeriod(targets, 'usedUnits', period, dealership, refDate);
      
      newUnits[period].value = newSales.count;
      newUnits[period].target = newUnitsTarget;
      newUnits[period].percent = calculatePercent(newSales.count, newUnitsTarget);
      newUnits[period].extra = formatCurrency(newSales.gross);

      usedUnits[period].value = usedSales.count;
      usedUnits[period].target = usedUnitsTarget;
      usedUnits[period].percent = calculatePercent(usedSales.count, usedUnitsTarget);
      usedUnits[period].extra = formatCurrency(usedSales.gross);

      const totalGross = newSales.gross + usedSales.gross;
      const totalUnits = newSales.count + usedSales.count;

      totalFrontend[period].value = totalGross;
      totalFrontend[period].target = grossTarget;
      totalFrontend[period].percent = calculatePercent(totalGross, grossTarget);

      perUnit[period].value = totalUnits > 0 ? totalGross / totalUnits : 0;
      perUnit[period].target = unitsTarget > 0 ? grossTarget / unitsTarget : 0;
      perUnit[period].percent = calculatePercent(perUnit[period].value, perUnit[period].target);
    });

    // Calculate F&I (Finance & Insurance) metrics
    const fiReserveGP = initMetricBlock();
    const fiProductGP = initMetricBlock();
    const fiTotalGP = initMetricBlock();
    const fiPerUnitSold = initMetricBlock();
    const avgOverUnderPerBike = initMetricBlock();

    ['daily', 'weekly', 'monthly', 'yearly'].forEach(period => {
      const periodActivities = period === 'daily' ? dailyActivities :
                               period === 'weekly' ? weeklyActivities :
                               period === 'monthly' ? monthlyActivities : yearlyActivities;

      // Sum F&I Reserve and Product from sales activities. Doc fee is back-end
      // gross too, so it joins the total while the reserve and product
      // breakdowns stay as F&I actually wrote them.
      const fiReserve = periodActivities.reduce((sum, a) => sum + (Number(a.fiReserve) || 0), 0);
      const fiProduct = periodActivities.reduce((sum, a) => sum + (Number(a.fiProduct) || 0), 0);
      const fiDocFee = docFeeIncome(periodActivities, getDocFee(targets));
      const fiTotal = fiReserve + fiProduct + fiDocFee;

      // Get targets: Back End GP for Reserve, F&I GP for Product
      const backEndTarget = getTargetForPeriod(targets, 'backEndGrossProfit', period, dealership, refDate);
      const fiTarget = getTargetForPeriod(targets, 'fiGrossProfit', period, dealership, refDate);
      const combinedTarget = backEndTarget + fiTarget;

      fiReserveGP[period].value = fiReserve;
      fiReserveGP[period].target = backEndTarget;
      fiReserveGP[period].percent = calculatePercent(fiReserve, backEndTarget);

      fiProductGP[period].value = fiProduct;
      fiProductGP[period].target = fiTarget;
      fiProductGP[period].percent = calculatePercent(fiProduct, fiTarget);

      fiTotalGP[period].value = fiTotal;
      fiTotalGP[period].target = combinedTarget;
      fiTotalGP[period].percent = calculatePercent(fiTotal, combinedTarget);

      // Calculate F&I per unit sold (total F&I / units sold)
      const totalUnitsSold = newUnits[period].value + usedUnits[period].value;
      const fiPerUnit = totalUnitsSold > 0 ? fiTotal / totalUnitsSold : 0;
      const unitsTarget = getTargetForPeriod(targets, 'unitSales', period, dealership, refDate);
      const fiPerUnitTarget = unitsTarget > 0 ? combinedTarget / unitsTarget : 0;

      fiPerUnitSold[period].value = fiPerUnit;
      fiPerUnitSold[period].target = fiPerUnitTarget;
      fiPerUnitSold[period].percent = calculatePercent(fiPerUnit, fiPerUnitTarget);

      // Average over/under across the deals that actually carried a trade.
      // There's no target for it — it's a discipline read, not a goal — so the
      // card shows the average and how many deals it's drawn from. Null (no
      // trades in the period) renders as an em dash rather than a false $0.
      avgOverUnderPerBike[period].value = avgOverUnder(periodActivities);
      avgOverUnderPerBike[period].extra = tradeCount(periodActivities);
    });

    // Calculate Average $ Per Bike metrics (legacy - kept for backward compatibility)
    const avgPerBike = initMetricBlock();
    const totalPAAmount = initMetricBlock();
    const percentWithAccessories = initMetricBlock();

    ['daily', 'weekly', 'monthly', 'yearly'].forEach(period => {
      const periodActivities = period === 'daily' ? dailyActivities :
                               period === 'weekly' ? weeklyActivities :
                               period === 'monthly' ? monthlyActivities : yearlyActivities;

      // Total bikes sold
      const totalBikes = periodActivities.length;

      // Total P&A amount (fiPartsAmount + fiApparelAmount)
      const totalPA = periodActivities.reduce((sum, a) => {
        const parts = Number(a.fiPartsAmount) || 0;
        const apparel = Number(a.fiApparelAmount) || 0;
        return sum + parts + apparel;
      }, 0);

      // Count bikes with accessories (where fiPartsAmount + fiApparelAmount > 0)
      const bikesWithAccessories = periodActivities.filter(a => {
        const parts = Number(a.fiPartsAmount) || 0;
        const apparel = Number(a.fiApparelAmount) || 0;
        return (parts + apparel) > 0;
      }).length;

      // Calculate metrics
      const avgDollarsPerBike = totalBikes > 0 ? totalPA / totalBikes : 0;
      const percentWith = totalBikes > 0 ? Math.round((bikesWithAccessories / totalBikes) * 100) : 0;

      avgPerBike[period].value = avgDollarsPerBike;
      avgPerBike[period].target = 0; // No target for this metric
      avgPerBike[period].percent = 0;
      avgPerBike[period].extra = totalBikes; // Store total bikes count

      totalPAAmount[period].value = totalPA;
      totalPAAmount[period].target = 0;
      totalPAAmount[period].percent = 0;

      percentWithAccessories[period].value = percentWith;
      percentWithAccessories[period].target = 0;
      percentWithAccessories[period].percent = 0;
    });

    // Calculate individual add-on metrics (Parts, Labor, Apparel)
    const partsAddOn = initMetricBlock();
    const laborAddOn = initMetricBlock();
    const apparelAddOn = initMetricBlock();

    // Get deal add-on targets from fixedOps targets
    const fixedOpsTargets = targetsData.fixedOps || {};
    const dealAddOnTargets = fixedOpsTargets.dealAddOnTargets || { parts: 600, labor: 300, apparel: 100 };

    ['daily', 'weekly', 'monthly', 'yearly'].forEach(period => {
      const periodActivities = period === 'daily' ? dailyActivities :
                               period === 'weekly' ? weeklyActivities :
                               period === 'monthly' ? monthlyActivities : yearlyActivities;

      // Total bikes sold
      const totalBikes = periodActivities.length;

      // Calculate individual category totals
      const totalParts = periodActivities.reduce((sum, a) => sum + (Number(a.fiPartsAmount) || 0), 0);
      const totalLabor = periodActivities.reduce((sum, a) => sum + (Number(a.fiServiceAmount) || 0), 0);
      const totalApparel = periodActivities.reduce((sum, a) => sum + (Number(a.fiApparelAmount) || 0), 0);

      // Count bikes with each category
      const bikesWithParts = periodActivities.filter(a => (Number(a.fiPartsAmount) || 0) > 0).length;
      const bikesWithLabor = periodActivities.filter(a => (Number(a.fiServiceAmount) || 0) > 0).length;
      const bikesWithApparel = periodActivities.filter(a => (Number(a.fiApparelAmount) || 0) > 0).length;

      // Calculate average per bike
      const avgPartsPerBike = totalBikes > 0 ? totalParts / totalBikes : 0;
      const avgLaborPerBike = totalBikes > 0 ? totalLabor / totalBikes : 0;
      const avgApparelPerBike = totalBikes > 0 ? totalApparel / totalBikes : 0;

      // Calculate percentage with each category
      const percentWithParts = totalBikes > 0 ? Math.round((bikesWithParts / totalBikes) * 100) : 0;
      const percentWithLabor = totalBikes > 0 ? Math.round((bikesWithLabor / totalBikes) * 100) : 0;
      const percentWithApparel = totalBikes > 0 ? Math.round((bikesWithApparel / totalBikes) * 100) : 0;

      // Parts metrics
      partsAddOn[period].value = avgPartsPerBike;
      partsAddOn[period].target = Number(dealAddOnTargets.parts) || 600;
      partsAddOn[period].percent = calculatePercent(avgPartsPerBike, partsAddOn[period].target);
      partsAddOn[period].extra = {
        totalBikes,
        totalAmount: totalParts,
        percentWith: percentWithParts
      };

      // Labor metrics
      laborAddOn[period].value = avgLaborPerBike;
      laborAddOn[period].target = Number(dealAddOnTargets.labor) || 300;
      laborAddOn[period].percent = calculatePercent(avgLaborPerBike, laborAddOn[period].target);
      laborAddOn[period].extra = {
        totalBikes,
        totalAmount: totalLabor,
        percentWith: percentWithLabor
      };

      // Apparel metrics
      apparelAddOn[period].value = avgApparelPerBike;
      apparelAddOn[period].target = Number(dealAddOnTargets.apparel) || 100;
      apparelAddOn[period].percent = calculatePercent(avgApparelPerBike, apparelAddOn[period].target);
      apparelAddOn[period].extra = {
        totalBikes,
        totalAmount: totalApparel,
        percentWith: percentWithApparel
      };
    });

    // Calculate transaction metrics using transaction data from parts and apparel sales
    const transactionsToSales = initMetricBlock();
    const transactionsToSitDowns = initMetricBlock();
    const transactionsToGreets = initMetricBlock();

    // Get target ratios from frontEnd targets
    const transactionsRatioTarget = Number(targets.transactionsRatio) || 20;
    const transactionsToSitDownsRatioTarget = Number(targets.transactionsToSitDownsRatio) || 5;
    const transactionsToGreetsRatioTarget = Number(targets.transactionsToGreetsRatio) || 2;

    // Activity funnel ratios (per unit sold) — lower is better. Standard 10:4:3:2:1.
    const greetsToSales = initMetricBlock();
    const sitDownsToSales = initMetricBlock();
    const testRidesToSales = initMetricBlock();
    const writeUpsToSales = initMetricBlock();
    const greetsRatioTarget = Number(targets.greetsRatio) || 10;
    const sitDownsRatioTarget = Number(targets.sitDownsRatio) || 4;
    const testRidesRatioTarget = Number(targets.testRidesRatio) || 3;
    const writeUpsRatioTarget = Number(targets.writeUpsRatio) || 2;

    console.log('Transaction metrics debug:', {
      dealershipId,
      transactionMetrics,
      newUnitsMonthly: newUnits.monthly?.value,
      sitDownsMonthly: sitDowns.monthly?.value,
      greetsMonthly: greets.monthly?.value
    });

    ['daily', 'weekly', 'monthly', 'yearly'].forEach(period => {
      const periodTransactions = transactionMetrics[period] || 0;
      const totalSales = newUnits[period].value + usedUnits[period].value;
      const totalSitDownsValue = sitDowns[period].value;
      const totalGreetsValue = greets[period].value;

      // Transactions to Sales (20:1 target means 20 transactions per 1 sale)
      const actualTransToSalesRatio = totalSales > 0 ? periodTransactions / totalSales : 0;
      transactionsToSales[period].value = actualTransToSalesRatio;
      transactionsToSales[period].target = transactionsRatioTarget;
      transactionsToSales[period].percent = calculatePercent(actualTransToSalesRatio, transactionsRatioTarget, true);
      transactionsToSales[period].extra = {
        totalTransactions: periodTransactions,
        totalSales: totalSales
      };

      // Transactions to Sit-Downs (5:1 target means 5 transactions per 1 sit-down)
      const actualTransToSitDownsRatio = totalSitDownsValue > 0 ? periodTransactions / totalSitDownsValue : 0;
      transactionsToSitDowns[period].value = actualTransToSitDownsRatio;
      transactionsToSitDowns[period].target = transactionsToSitDownsRatioTarget;
      transactionsToSitDowns[period].percent = calculatePercent(actualTransToSitDownsRatio, transactionsToSitDownsRatioTarget, true);
      transactionsToSitDowns[period].extra = {
        totalTransactions: periodTransactions,
        totalSitDowns: totalSitDownsValue
      };

      // Transactions to Greets (2:1 target means 2 transactions per 1 greet)
      const actualTransToGreetsRatio = totalGreetsValue > 0 ? periodTransactions / totalGreetsValue : 0;
      transactionsToGreets[period].value = actualTransToGreetsRatio;
      transactionsToGreets[period].target = transactionsToGreetsRatioTarget;
      transactionsToGreets[period].percent = calculatePercent(actualTransToGreetsRatio, transactionsToGreetsRatioTarget, true);
      transactionsToGreets[period].extra = {
        totalTransactions: periodTransactions,
        totalGreets: totalGreetsValue
      };

      // Activity funnel ratios per unit sold (lower is better)
      const totalWriteUpsValue = writeUps[period].value;
      const totalTestRidesValue = testRides[period].value;

      const greetsPerSale = totalSales > 0 ? totalGreetsValue / totalSales : 0;
      greetsToSales[period].value = greetsPerSale;
      greetsToSales[period].target = greetsRatioTarget;
      greetsToSales[period].percent = calculatePercent(greetsPerSale, greetsRatioTarget, true);
      greetsToSales[period].extra = { activityCount: totalGreetsValue, totalSales };

      const sitDownsPerSale = totalSales > 0 ? totalSitDownsValue / totalSales : 0;
      sitDownsToSales[period].value = sitDownsPerSale;
      sitDownsToSales[period].target = sitDownsRatioTarget;
      sitDownsToSales[period].percent = calculatePercent(sitDownsPerSale, sitDownsRatioTarget, true);
      sitDownsToSales[period].extra = { activityCount: totalSitDownsValue, totalSales };

      const testRidesPerSale = totalSales > 0 ? totalTestRidesValue / totalSales : 0;
      testRidesToSales[period].value = testRidesPerSale;
      testRidesToSales[period].target = testRidesRatioTarget;
      testRidesToSales[period].percent = calculatePercent(testRidesPerSale, testRidesRatioTarget, true);
      testRidesToSales[period].extra = { activityCount: totalTestRidesValue, totalSales };

      const writeUpsPerSale = totalSales > 0 ? totalWriteUpsValue / totalSales : 0;
      writeUpsToSales[period].value = writeUpsPerSale;
      writeUpsToSales[period].target = writeUpsRatioTarget;
      writeUpsToSales[period].percent = calculatePercent(writeUpsPerSale, writeUpsRatioTarget, true);
      writeUpsToSales[period].extra = { activityCount: totalWriteUpsValue, totalSales };
    });

    return {
      greets,
      sitDowns,
      writeUps,
      testRides,
      newUnits,
      usedUnits,
      totalFrontend,
      perUnit,
      fiReserveGP,
      fiProductGP,
      fiTotalGP,
      fiPerUnitSold,
      avgOverUnderPerBike,
      avgPerBike,
      totalPAAmount,
      percentWithAccessories,
      partsAddOn,
      laborAddOn,
      apparelAddOn,
      transactionsToSales,
      transactionsToSitDowns,
      transactionsToGreets,
      greetsToSales,
      sitDownsToSales,
      testRidesToSales,
      writeUpsToSales,
      hasTargets: Array.isArray(targets.monthlyTargets) && targets.monthlyTargets.length > 0,
    };
    } catch (err) {
      console.error(`Error fetching metrics for dealership ${dealershipId}:`, err);
      return {
        greets: initMetricBlock(),
        sitDowns: initMetricBlock(),
        writeUps: initMetricBlock(),
        testRides: initMetricBlock(),
        newUnits: initMetricBlock(),
        usedUnits: initMetricBlock(),
        totalFrontend: initMetricBlock(),
        perUnit: initMetricBlock(),
        fiReserveGP: initMetricBlock(),
        fiProductGP: initMetricBlock(),
        fiTotalGP: initMetricBlock(),
        fiPerUnitSold: initMetricBlock(),
        avgOverUnderPerBike: initMetricBlock(),
        avgPerBike: initMetricBlock(),
        totalPAAmount: initMetricBlock(),
        percentWithAccessories: initMetricBlock(),
        partsAddOn: initMetricBlock(),
        laborAddOn: initMetricBlock(),
        apparelAddOn: initMetricBlock(),
        transactionsToSales: initMetricBlock(),
        transactionsToSitDowns: initMetricBlock(),
        transactionsToGreets: initMetricBlock(),
        greetsToSales: initMetricBlock(),
        sitDownsToSales: initMetricBlock(),
        testRidesToSales: initMetricBlock(),
        writeUpsToSales: initMetricBlock(),
        hasTargets: false,
      };
    }
  };

  const filteredDealerships = useMemo(() => {
    if (!selectedDealerships || selectedDealerships.length === 0) return dealerships;
    return dealerships.filter((d) => selectedDealerships.includes(d.id));
  }, [dealerships, selectedDealerships]);

  const toggleDealership = (id) => {
    setSelectedDealerships((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const selectAll = () => setSelectedDealerships(dealerships.map((d) => d.id));
  const clearAll = () => setSelectedDealerships([]);

  // Time-adjusted percentage class for progress bars
  const percentClass = (actual, target, period, fiscalMeta, lowerIsBetter = false) => {
    if (!target || target === 0) return 'low';
    if (lowerIsBetter) {
      // Ratios aren't time-cumulative, so compare the ratio to target directly
      // (no day-of-period pacing). At or under target = good.
      if (!actual || actual <= 0) return 'low';
      const perf = target / actual;
      if (perf >= 0.9) return 'high';
      if (perf >= 0.7) return 'mid';
      return 'low';
    }
    const today = new Date(selectedDate);
    let expectedProgressPercent = 0;
    const actualProgressPercent = (actual / target) * 100;

    switch (period) {
      case 'daily':
        expectedProgressPercent = 100;
        break;
      case 'weekly':
        {
          const dayOfWeek = today.getDay();
          const daysIntoWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          expectedProgressPercent = ((daysIntoWeek + 1) / 7) * 100;
        }
        break;
      case 'monthly':
        {
          const daysIntoMonth = today.getDate();
          const daysInCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
          expectedProgressPercent = (daysIntoMonth / daysInCurrentMonth) * 100;
        }
        break;
      case 'yearly':
        expectedProgressPercent = (fiscalMeta.daysElapsed / fiscalMeta.daysInYear) * 100;
        break;
      default:
        expectedProgressPercent = 100;
    }

    const performanceRatio = actualProgressPercent / expectedProgressPercent;
    if (performanceRatio >= 0.9) return 'high';  // On track (green)
    if (performanceRatio >= 0.7) return 'mid';   // Caution (yellow)
    return 'low';  // Behind (red)
  };

  // percentClass is already pace-adjusted, so the card colour and the meter
  // colour come from the same reading and can never disagree.
  const statusOf = (data, period, fiscalMeta, lowerIsBetter = false) => {
    if (!data || !data.target) return 'neutral';
    const cls = percentClass(data.value, data.target, period, fiscalMeta, lowerIsBetter);
    return cls === 'high' ? 'ontrack' : cls === 'mid' ? 'caution' : 'behind';
  };

  const statusToMeter = (status) =>
    status === 'ontrack' ? 'high' : status === 'caution' ? 'mid' : status === 'behind' ? 'low' : '';

  // One range selected is the normal case, and then the range is already named
  // once in the View selector — repeating "Monthly" on every card was noise.
  // With two or more selected each block has to name itself again.
  const multiPeriod = selectedPeriods.length > 1;

  // With a single range the whole card takes that range's status. With several
  // there is no one status for the card, so each block carries its own.
  const cardStatus = (block, fiscalMeta, lowerIsBetter = false) =>
    multiPeriod ? 'neutral' : statusOf(block[selectedPeriods[0]], selectedPeriods[0], fiscalMeta, lowerIsBetter);

  // Trend indicator utilities (period-aware and fiscal-aware)
  const calculateTrendIndicator = (actual, target, period, fiscalMeta, lowerIsBetter = false) => {
    if (!target || target === 0) return { status: 'neutral', color: 'rgba(255,255,255,0.5)' };
    if (lowerIsBetter) {
      // Direct ratio comparison (no pacing); at or under target = on track.
      if (!actual || actual <= 0) return { status: 'behind', color: '#ef4444' };
      const perf = target / actual;
      if (perf >= 0.9) return { status: 'onTrack', color: '#10b981' };
      if (perf >= 0.7) return { status: 'caution', color: '#f59e0b' };
      return { status: 'behind', color: '#ef4444' };
    }
    const today = new Date(selectedDate);
    let expectedProgressPercent = 0;
    const actualProgressPercent = (actual / target) * 100;
    switch (period) {
      case 'daily':
        expectedProgressPercent = 100;
        break;
      case 'weekly':
        {
          const dayOfWeek = today.getDay();
          const daysIntoWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          expectedProgressPercent = ((daysIntoWeek + 1) / 7) * 100;
        }
        break;
      case 'monthly':
        {
          const daysIntoMonth = today.getDate();
          const daysInCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
          expectedProgressPercent = (daysIntoMonth / daysInCurrentMonth) * 100;
        }
        break;
      case 'yearly':
        expectedProgressPercent = (fiscalMeta.daysElapsed / fiscalMeta.daysInYear) * 100;
        break;
      default:
        expectedProgressPercent = 100;
    }
    const performanceRatio = actualProgressPercent / expectedProgressPercent;
    if (performanceRatio >= 0.9) {
      return { status: 'onTrack', color: '#10b981' };
    } else if (performanceRatio >= 0.7) {
      return { status: 'caution', color: '#f59e0b' };
    } else {
      return { status: 'behind', color: '#ef4444' };
    }
  };

  const TrendIndicator = ({ actual, target, period, fiscalMeta, lowerIsBetter = false }) => {
    const trend = calculateTrendIndicator(actual, target, period, fiscalMeta, lowerIsBetter);
    const isOnTrack = trend.status === 'onTrack';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <svg width="16" height="16" viewBox="0 0 16 16" style={{ marginBottom: isOnTrack ? '2px' : '0px' }}>
          {isOnTrack ? (
            <polygon points="8,2 14,12 2,12" fill={trend.color} />
          ) : (
            <polygon points="2,4 14,4 8,14" fill={trend.color} />
          )}
        </svg>
        <span style={{ fontSize: '12px', color: trend.color, fontWeight: '600' }}>
          {trend.status === 'onTrack' ? 'On Track' : trend.status === 'caution' ? 'Caution' : 'Behind'}
        </span>
      </div>
    );
  };

  const renderKpi = (title, block, formatter = formatNumber, extraLabel, variant = 'default', fiscalMeta) => {
    const isHero = variant === 'hero';
    return (
      <div
        className={`kpi-card${isHero ? ' perf-hero' : ''}${variant === 'highlight' ? ' kpi-card-highlight' : ''}`}
        data-status={cardStatus(block, fiscalMeta)}
      >
        <div className="kpi-label">
          <span>{title}</span>
        </div>
        <div className={`kpi-compare-multi${multiPeriod ? ' multi' : ''}`}>
          {selectedPeriods.map(period => {
            const data = block[period];
            const status = statusOf(data, period, fiscalMeta);
            return (
              <div key={period} className="kpi-period-block" data-status={multiPeriod ? status : undefined}>
                {multiPeriod && <div className="kpi-sub">{periodLabels[period]}</div>}
                <div className="kpi-value">{formatter(data.value)}</div>
                <div className="kpi-target">Target: {formatter(data.target)}</div>
                <div className={`kpi-percent ${statusToMeter(status)}`}>{data.percent}%</div>
                <div className="kpi-meter-bar">
                  <div className={`kpi-meter-fill ${statusToMeter(status)}`} style={{ width: `${Math.min(data.percent, 100)}%` }}></div>
                </div>
                {extraLabel && data.extra && (
                  <div className="kpi-extra">{extraLabel}: {data.extra}</div>
                )}
                {/* Only the section total spells the status out. On every card
                    the words repeated what the colour already says. */}
                {isHero && fiscalMeta && (
                  <TrendIndicator actual={data.value} target={data.target} period={period} fiscalMeta={fiscalMeta} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Average over/under has no target — an over isn't a goal to hit, it's a
  // number to keep small — so it gets its own card without a meter or percent.
  // Sign is what matters: positive = money given away on trades, negative =
  // bought below book. The deal count tells you how much weight to give it.
  // No target means no pace to measure against, so the card takes its status
  // from the sign the same way the figure does.
  const overUnderStatus = (value) =>
    value === null || value === 0 ? 'neutral' : value > 0 ? 'behind' : 'ontrack';

  const renderOverUnderCard = (block) => {
    const firstValue = block[selectedPeriods[0]]?.value;
    return (
      <div className="kpi-card" data-status={multiPeriod ? 'neutral' : overUnderStatus(firstValue)}>
        <div className="kpi-label">
          <span>Avg Over/Under per Bike</span>
        </div>
        <div className={`kpi-compare-multi${multiPeriod ? ' multi' : ''}`}>
          {selectedPeriods.map(period => {
            const value = block[period].value;
            const deals = block[period].extra || 0;
            // An over costs gross, so it reads "low"; an under reads "high".
            const tone = value === null || value === 0 ? 'mid' : value > 0 ? 'low' : 'high';
            // formatCurrency abbreviates and would render "$-1.5K"; sign the
            // label explicitly and format the magnitude.
            const label = value === null
              ? '—'
              : `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatCurrency(Math.abs(value))}`;
            return (
              <div key={period} className="kpi-period-block" data-status={multiPeriod ? overUnderStatus(value) : undefined}>
                {multiPeriod && <div className="kpi-sub">{periodLabels[period]}</div>}
                <div className="kpi-value">{label}</div>
                <div className={`kpi-percent ${tone}`}>
                  {value === null ? 'No trades' : value > 0 ? 'Over' : value < 0 ? 'Under' : 'At book'}
                </div>
                <div className="kpi-target">
                  {deals === 0 ? '—' : `${deals} deal${deals === 1 ? '' : 's'} with a trade`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Custom render function for Average $/Bike card
  const renderAvgPerBikeCard = (avgBlock, totalBlock, percentBlock) => {
    return (
      <div className="kpi-card kpi-card-highlight">
        <div className="kpi-label">
          <span>Average $/Bike Sold</span>
        </div>
        <div className="kpi-compare-multi">
          {selectedPeriods.map(period => (
            <div key={period} className="kpi-period-block">
              <div className="kpi-sub">{periodLabels[period]}</div>
              {/* Large font for average $/bike */}
              <div className="kpi-value" style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
                {formatCurrency(avgBlock[period].value)}
              </div>
              {/* Show total bikes sold */}
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '12px' }}>
                Bikes sold: {avgBlock[period].extra || 0}
              </div>
              {/* Smaller font for total P&A */}
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>
                Total P&A: {formatCurrency(totalBlock[period].value)}
              </div>
              {/* Percentage with accessories */}
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                {percentBlock[period].value}% with accessories
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Custom render function for individual add-on cards (Parts, Labor, Apparel)
  const renderAddOnCard = (title, addOnBlock, fiscalMeta) => {
    return (
      <div className="kpi-card" data-status={cardStatus(addOnBlock, fiscalMeta)}>
        <div className="kpi-label">
          <span>{title}</span>
        </div>
        <div className={`kpi-compare-multi${multiPeriod ? ' multi' : ''}`}>
          {selectedPeriods.map(period => {
            const data = addOnBlock[period];
            const extra = data.extra || {};
            const status = statusOf(data, period, fiscalMeta);
            return (
              <div key={period} className="kpi-period-block" data-status={multiPeriod ? status : undefined}>
                {multiPeriod && <div className="kpi-sub">{periodLabels[period]}</div>}
                {/* Average $/bike */}
                <div className="kpi-value">{formatCurrency(data.value)}</div>
                <div className="kpi-target">Target: {formatCurrency(data.target)}</div>
                <div className={`kpi-percent ${statusToMeter(status)}`}>{data.percent}%</div>
                <div className="kpi-meter-bar">
                  <div className={`kpi-meter-fill ${statusToMeter(status)}`} style={{ width: `${Math.min(data.percent, 100)}%` }}></div>
                </div>
                {/* Additional details */}
                <div className="perf-detail">Bikes sold: {extra.totalBikes || 0}</div>
                <div className="perf-detail">Total sold: {formatCurrency(extra.totalAmount || 0)}</div>
                <div className="perf-detail">{extra.percentWith || 0}% with accessories</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTransactionRatioCard = (title, ratioBlock, targetRatio, fiscalMeta, variant) => {
    const isHero = variant === 'hero';
    return (
      <div className={`kpi-card${isHero ? ' perf-hero' : ''}`} data-status={cardStatus(ratioBlock, fiscalMeta, true)}>
        <div className="kpi-label">
          <span>{title}</span>
        </div>
        <div className={`kpi-compare-multi${multiPeriod ? ' multi' : ''}`}>
          {selectedPeriods.map(period => {
            const data = ratioBlock[period];
            const extra = data.extra || {};
            const status = statusOf(data, period, fiscalMeta, true);
            const formattedRatio = data.value > 0 ? `${data.value.toFixed(1)}:1` : '0:1';
            const formattedTarget = `${targetRatio}:1`;

            return (
              <div key={period} className="kpi-period-block" data-status={multiPeriod ? status : undefined}>
                {multiPeriod && <div className="kpi-sub">{periodLabels[period]}</div>}
                {/* Actual ratio */}
                <div className="kpi-value perf-ratio">{formattedRatio}</div>
                <div className="kpi-target">Target: {formattedTarget}</div>
                <div className={`kpi-percent ${statusToMeter(status)}`}>{data.percent}%</div>
                <div className="kpi-meter-bar">
                  <div className={`kpi-meter-fill ${statusToMeter(status)}`} style={{ width: `${Math.min(data.percent, 100)}%` }}></div>
                </div>
                {/* Additional details */}
                <div className="perf-detail">Total transactions: {extra.totalTransactions || 0}</div>
                <div className="perf-detail">
                  {title.includes('Sales') && `Total sales: ${extra.totalSales || 0}`}
                  {title.includes('Sit-Downs') && `Total sit-downs: ${extra.totalSitDowns || 0}`}
                  {title.includes('Greets') && `Total greets: ${extra.totalGreets || 0}`}
                </div>
                {isHero && fiscalMeta && (
                  <TrendIndicator actual={data.value} target={data.target} period={period} fiscalMeta={fiscalMeta} lowerIsBetter={true} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Funnel activity ratio card: {activity} per sale (lower is better).
  // countLabel is the sub-detail label for the activity count (e.g. "greets").
  const renderFunnelRatioCard = (title, ratioBlock, countLabel, fiscalMeta, variant) => {
    const isHero = variant === 'hero';
    return (
      <div className={`kpi-card${isHero ? ' perf-hero' : ''}`} data-status={cardStatus(ratioBlock, fiscalMeta, true)}>
        <div className="kpi-label">
          <span>{title}</span>
        </div>
        <div className={`kpi-compare-multi${multiPeriod ? ' multi' : ''}`}>
          {selectedPeriods.map(period => {
            const data = ratioBlock[period];
            const extra = data.extra || {};
            const status = statusOf(data, period, fiscalMeta, true);
            const formattedRatio = data.value > 0 ? `${data.value.toFixed(1)}:1` : '0:1';
            const formattedTarget = `${data.target}:1`;

            return (
              <div key={period} className="kpi-period-block" data-status={multiPeriod ? status : undefined}>
                {multiPeriod && <div className="kpi-sub">{periodLabels[period]}</div>}
                <div className="kpi-value perf-ratio">{formattedRatio}</div>
                <div className="kpi-target">Target: {formattedTarget}</div>
                <div className={`kpi-percent ${statusToMeter(status)}`}>{data.percent}%</div>
                <div className="kpi-meter-bar">
                  <div className={`kpi-meter-fill ${statusToMeter(status)}`} style={{ width: `${Math.min(data.percent, 100)}%` }}></div>
                </div>
                <div className="perf-detail">Total {countLabel}: {extra.activityCount || 0}</div>
                <div className="perf-detail">Total sales: {extra.totalSales || 0}</div>
                {isHero && fiscalMeta && (
                  <TrendIndicator actual={data.value} target={data.target} period={period} fiscalMeta={fiscalMeta} lowerIsBetter={true} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* A section is one headline number and its parts. The headline gets its own
     column so the relationship is legible; where a section has no single
     headline the parts fill the row instead. */
  const renderSection = (title, subtitle, hero, breakouts) => (
    <section className="perf-section" key={title}>
      <div className="perf-section-head">
        <h3>{title}</h3>
        <p>{subtitle}</p>
      </div>
      <div className={`perf-section-body kpi-compact${hero ? '' : ' no-hero'}`}>
        {hero}
        <div className="perf-breakouts">{breakouts}</div>
      </div>
    </section>
  );

  /* The section headlines, held at the top of the page. Reading across the
     strip answers "how is the store selling" without scrolling the detail. */
  const renderSummary = (m, fm) => {
    const p = selectedPeriods[0];
    const items = [
      ['New Bikes', m.newUnits, formatNumber, false],
      ['Used Bikes', m.usedUnits, formatNumber, false],
      ['Total Frontend', m.totalFrontend, formatCurrency, false],
      ['Frontend / Unit', m.perUnit, formatCurrency, false],
      ['F&I Total', m.fiTotalGP, formatCurrency, false],
      ['Trans. per Sale', m.transactionsToSales, (v) => (v > 0 ? `${v.toFixed(1)}:1` : '0:1'), true],
    ];
    return (
      <div className="perf-summary">
        {items.map(([label, block, fmt, lowerIsBetter]) => {
          const data = block?.[p];
          if (!data) return null;
          return (
            <div
              className="perf-summary-item"
              key={label}
              data-status={multiPeriod ? 'neutral' : statusOf(data, p, fm, lowerIsBetter)}
            >
              <div className="perf-summary-label">{label}</div>
              <div className="perf-summary-value">{fmt(data.value)}</div>
              <div className="perf-summary-meta">
                <span className="perf-pct">{data.percent}%</span>
                of {fmt(data.target)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const startDate = useMemo(() => {
    // First day of the month for the date the user has selected in the picker
    return toLocalDateString(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
  }, [selectedDate]);
  const endDate = useMemo(() => {
    return toLocalDateString(selectedDate);
  }, [selectedDate]);

  if (loading) {
    return <div className="placeholder-view"><p>Loading sales performance...</p></div>;
  }

  if (error) {
    return <div className="placeholder-view error"><p>{error}</p></div>;
  }

  if (filteredDealerships.length > 0 && filteredDealerships.every(d => !metricsByDealer[d.id]?.hasTargets)) {
    return <NoTargetsPlaceholder />;
  }

  return (
    <div className="dashboard-view compact-dash perf-dash">
      <div className="view-header">
        <div>
          <h1 className="view-title">Sales Performance Dashboard</h1>
          <p className="view-subtitle">{periodLabels[selectedPeriods[0]]} & {periodLabels[selectedPeriods[1]]} performance across dealerships</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar sales-performance">
        <div className="filter-bar-main">
          <button 
            className={`filter-button ${selectedDealerships.length > 0 ? 'has-filters' : ''}`}
            onClick={() => setShowFilterPanel(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 4h18M3 12h12M3 20h6" strokeLinecap="round"/>
            </svg>
            Filters
            {selectedDealerships.length > 0 && ` (${selectedDealerships.length})`}
          </button>

          <div className="period-selector">
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginRight: '8px' }}>View:</span>
            {periodOrder.map(period => (
              <button
                key={period}
                className={`period-btn ${selectedPeriods.includes(period) ? 'active' : ''}`}
                onClick={() => togglePeriod(period)}
              >
                {periodLabels[period]}
              </button>
            ))}
          </div>

          <DatePicker
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            label=""
          />
        </div>

        {selectedDealerships.length > 0 && (
          <div className="active-filters">
            {dealerships
              .filter((d) => selectedDealerships.includes(d.id))
              .map((d) => (
                <div key={d.id} className="active-filter-chip">
                  {d.name}
                  <button onClick={() => toggleDealership(d.id)}>×</button>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <>
          <div className="filter-overlay open" onClick={() => setShowFilterPanel(false)} />
          <div className="filter-panel open">
            <div className="filter-panel-header">
              <h3>Filters</h3>
              <button className="filter-panel-close" onClick={() => setShowFilterPanel(false)}>×</button>
            </div>

            <div className="filter-panel-content">
              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>Dealerships</h4>
                  <button onClick={selectedDealerships.length === 0 ? selectAll : clearAll} className="btn-select-all">
                    {selectedDealerships.length === 0 ? 'Select All' : 'Clear All'}
                  </button>
                </div>
                {dealerships.map(dealership => (
                  <label key={dealership.id} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDealerships.includes(dealership.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDealerships([...selectedDealerships, dealership.id]);
                        } else {
                          setSelectedDealerships(selectedDealerships.filter(d => d !== dealership.id));
                        }
                      }}
                    />
                    <span>{dealership.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* --- Leaderboard Widget --- */}
      <SalesLeaderboard
        selectedDealerships={selectedDealerships}
        startDate={startDate}
        endDate={endDate}
        period={leaderboardPeriod}
        onPeriodChange={setLeaderboardPeriod}
      />

      {filteredDealerships.length <= 1 ? (
        filteredDealerships.map((d) => {
          const m = metricsByDealer[d.id];
          if (!m) return null;
          const fm = getFiscalMetaForDealership(d, selectedDate);
          return (
            <div key={d.id} className="card">
              <h2 className="perf-dealer-name">{d.name}</h2>

              {!multiPeriod && renderSummary(m, fm)}

              {renderSection(
                'Bike Sales',
                'Units sold and the frontend gross they produced',
                renderKpi('Total Frontend $', m.totalFrontend, formatCurrency, undefined, 'hero', fm),
                <>
                  {renderKpi('New Bikes Sold', m.newUnits, formatNumber, 'Gross', 'default', fm)}
                  {renderKpi('Used Bikes Sold', m.usedUnits, formatNumber, 'Gross', 'default', fm)}
                  {renderKpi('Frontend per Unit', m.perUnit, formatCurrency, undefined, 'default', fm)}
                </>
              )}

              {renderSection(
                'F&I Gross Profit',
                'Reserve and product gross, and what each bike carried',
                renderKpi('F&I Total', m.fiTotalGP, formatCurrency, undefined, 'hero', fm),
                <>
                  {renderKpi('F&I Reserve GP', m.fiReserveGP, formatCurrency, undefined, 'default', fm)}
                  {renderKpi('F&I Product GP', m.fiProductGP, formatCurrency, undefined, 'default', fm)}
                  {renderKpi('F&I Per Unit Sold', m.fiPerUnitSold, formatCurrency, undefined, 'default', fm)}
                  {renderOverUnderCard(m.avgOverUnderPerBike)}
                </>
              )}

              {renderSection(
                'Transactions',
                'Transactions worked for each sale, sit-down, and greet',
                renderTransactionRatioCard('Transactions per Sale', m.transactionsToSales, m.transactionsToSales.monthly.target, fm, 'hero'),
                <>
                  {renderTransactionRatioCard('Transactions per Sit-Down', m.transactionsToSitDowns, m.transactionsToSitDowns.monthly.target, fm)}
                  {renderTransactionRatioCard('Transactions per Greet', m.transactionsToGreets, m.transactionsToGreets.monthly.target, fm)}
                </>
              )}

              {renderSection(
                'Traffic & Engagement',
                'Activity worked for each sale — lower is better',
                renderFunnelRatioCard('Greets per Sale', m.greetsToSales, 'greets', fm, 'hero'),
                <>
                  {renderFunnelRatioCard('Sit-Downs per Sale', m.sitDownsToSales, 'sit-downs', fm)}
                  {renderFunnelRatioCard('Test Rides per Sale', m.testRidesToSales, 'test rides', fm)}
                  {renderFunnelRatioCard('Write-Ups per Sale', m.writeUpsToSales, 'write-ups', fm)}
                </>
              )}

              {renderSection(
                'Deal Add-ons per Bike',
                'Average parts, labor, and apparel written on each deal',
                null,
                <>
                  {renderAddOnCard('Parts', m.partsAddOn, fm)}
                  {renderAddOnCard('Labor', m.laborAddOn, fm)}
                  {renderAddOnCard('Apparel & Licensing', m.apparelAddOn, fm)}
                </>
              )}
            </div>
          );
        })
      ) : (
        <>
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Traffic & Engagement</h2>
              <p className="card-subtitle">Greets, sit-downs, test rides, and write-ups per sale (lower is better)</p>
            </div>
            <div className="kpi-compare-row">
              {filteredDealerships.map((d) => {
                const m = metricsByDealer[d.id];
                if (!m) return null;
                return (
                  <div key={`traffic-${d.id}`} className="kpi-compact">
                    <div className="kpi-dealer-label">{d.name}</div>
                    {renderFunnelRatioCard('Greets per Sale', m.greetsToSales, 'greets', getFiscalMetaForDealership(d, selectedDate))}
                    {renderFunnelRatioCard('Sit-Downs per Sale', m.sitDownsToSales, 'sit-downs', getFiscalMetaForDealership(d, selectedDate))}
                    {renderFunnelRatioCard('Test Rides per Sale', m.testRidesToSales, 'test rides', getFiscalMetaForDealership(d, selectedDate))}
                    {renderFunnelRatioCard('Write-Ups per Sale', m.writeUpsToSales, 'write-ups', getFiscalMetaForDealership(d, selectedDate))}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Transactions</h2>
              <p className="card-subtitle">Transaction ratios compared to sales, sit-downs, and greets</p>
            </div>
            <div className="kpi-compare-row">
              {filteredDealerships.map((d) => {
                const m = metricsByDealer[d.id];
                if (!m) return null;
                return (
                  <div key={`transactions-${d.id}`} className="kpi-compact kpi-sales-group">
                    <div className="kpi-dealer-label">{d.name}</div>
                    {renderTransactionRatioCard('Transactions per Sale', m.transactionsToSales, m.transactionsToSales.monthly.target, getFiscalMetaForDealership(d, selectedDate))}
                    {renderTransactionRatioCard('Transactions per Sit-Down', m.transactionsToSitDowns, m.transactionsToSitDowns.monthly.target, getFiscalMetaForDealership(d, selectedDate))}
                    {renderTransactionRatioCard('Transactions per Greet', m.transactionsToGreets, m.transactionsToGreets.monthly.target, getFiscalMetaForDealership(d, selectedDate))}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Sales Performance</h2>
              <p className="card-subtitle">New/used units, gross, and per unit</p>
            </div>
            <div className="kpi-compare-row">
              {filteredDealerships.map((d) => {
                const m = metricsByDealer[d.id];
                if (!m) return null;
                return (
                  <div key={`sales-${d.id}`} className="kpi-compact kpi-sales-group">
                    <div className="kpi-dealer-label">{d.name}</div>
                    {renderKpi('New Units', m.newUnits, formatNumber, 'Gross', 'default', getFiscalMetaForDealership(d, selectedDate))}
                    {renderKpi('Used Units', m.usedUnits, formatNumber, 'Gross', 'default', getFiscalMetaForDealership(d, selectedDate))}
                    {renderKpi('Total Frontend $', m.totalFrontend, formatCurrency, undefined, 'highlight', getFiscalMetaForDealership(d, selectedDate))}
                    {renderKpi('Frontend per Unit', m.perUnit, formatCurrency, undefined, 'highlight', getFiscalMetaForDealership(d, selectedDate))}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="card-title">F&I Gross Profit</h2>
              <p className="card-subtitle">Finance & Insurance revenue metrics</p>
            </div>
            <div className="kpi-compare-row">
              {filteredDealerships.map((d) => {
                const m = metricsByDealer[d.id];
                if (!m) return null;
                return (
                  <div key={`fi-${d.id}`} className="kpi-compact kpi-sales-group">
                    <div className="kpi-dealer-label">{d.name}</div>
                    {renderKpi('F&I Reserve GP', m.fiReserveGP, formatCurrency, undefined, 'default', getFiscalMetaForDealership(d, selectedDate))}
                    {renderKpi('F&I Product GP', m.fiProductGP, formatCurrency, undefined, 'default', getFiscalMetaForDealership(d, selectedDate))}
                    {renderKpi('F&I Total', m.fiTotalGP, formatCurrency, undefined, 'highlight', getFiscalMetaForDealership(d, selectedDate))}
                    {renderKpi('F&I Per Unit Sold', m.fiPerUnitSold, formatCurrency, undefined, 'default', getFiscalMetaForDealership(d, selectedDate))}
                    {renderOverUnderCard(m.avgOverUnderPerBike)}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Deal Add-ons</h2>
              <p className="card-subtitle">Average parts, labor, and apparel revenue per motorcycle sold</p>
            </div>
            <div className="kpi-compare-row">
              {filteredDealerships.map((d) => {
                const m = metricsByDealer[d.id];
                if (!m) return null;
                return (
                  <div key={`addons-${d.id}`} className="kpi-compact kpi-sales-group">
                    <div className="kpi-dealer-label">{d.name}</div>
                    {renderAddOnCard('Parts', m.partsAddOn, getFiscalMetaForDealership(d, selectedDate))}
                    {renderAddOnCard('Labor', m.laborAddOn, getFiscalMetaForDealership(d, selectedDate))}
                    {renderAddOnCard('Apparel & Licensing', m.apparelAddOn, getFiscalMetaForDealership(d, selectedDate))}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
